import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import Footer from '../components/layout/Footer';
import { trackBookingPageViewed, trackCalendlyBookingCompleted } from '../utils/analytics';

const CALENDLY_URL = 'https://calendly.com/orderflowschool-support/30min';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Timezone helper ─── */
const getTimezoneInfo = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone, now = new Date();
    const ef = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false });
    const uf = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
    const ep = ef.formatToParts(now), up = uf.formatToParts(now);
    const et = parseInt(ep.find(p => p.type === 'hour').value) * 60 + parseInt(ep.find(p => p.type === 'minute').value);
    const ut = parseInt(up.find(p => p.type === 'hour').value) * 60 + parseInt(up.find(p => p.type === 'minute').value);
    let d = ut - et; if (d > 720) d -= 1440; if (d < -720) d += 1440;
    const to12 = h => {
      let l = h + d / 60; if (l >= 24) l -= 24; if (l < 0) l += 24;
      const hh = Math.floor(l), m = Math.round((l - hh) * 60), p = hh >= 12 ? 'PM' : 'AM', dh = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
      return m === 0 ? `${dh}:00 ${p}` : `${dh}:${String(m).padStart(2, '0')} ${p}`;
    };
    return { tz, absDiff: Math.abs(d / 60), localStart: to12(9), localEnd: to12(20) };
  } catch { return { tz: '', absDiff: 0, localStart: '', localEnd: '' }; }
};

/* ─── Scroll reveal hook ─── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('s-in'); io.unobserve(el); } },
      { threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`s-fade ${className}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
};

/* ─── Label badge ─── */
const Label = ({ children, color = '#C9A235' }) => (
  <span
    className="inline-block"
    style={{
      border: `1px solid ${color === '#C9A235' ? 'rgba(201,162,53,0.4)' : 'rgba(0,217,126,0.35)'}`,
      background: color === '#C9A235' ? 'rgba(201,162,53,0.07)' : 'rgba(0,217,126,0.08)',
      borderRadius: 2, padding: '5px 14px',
      fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 10,
      textTransform: 'uppercase', letterSpacing: '0.18em', color,
    }}
  >
    {children}
  </span>
);

/* ─── Feature icon ─── */
const FeatureIcon = ({ d }) => (
  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,162,53,0.12)' }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A235" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  </div>
);

/* ─── Features ─── */
const features = [
  {
    icon: 'M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z',
    title: '80+ STRUCTURED VIDEO LESSONS',
    body: "So you can build the complete order flow framework from foundations to advanced execution — without having to start your trading from scratch.",
  },
  {
    icon: 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z',
    title: 'LIVE WEEKLY TRADING SESSIONS',
    body: "So you can watch order flow being read on real markets, in real time, every single week — and start seeing what funded traders actually see.",
  },
  {
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    title: 'WEEKEND MARKET PREP CALLS',
    body: "So you can walk into every trading week knowing exactly what to look for, where liquidity sits, and how to position yourself before the open.",
  },
  {
    icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
    title: '1-ON-1 ACCESS TO EDGAR',
    body: "So you can get direct feedback on your specific trades, setups, and execution — not generic advice, your actual trading.",
  },
  {
    icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
    title: 'OFS PRIVATE DISCORD COMMUNITY',
    body: "So you can trade alongside serious traders using the same framework — and never figure this out alone.",
  },
];

const Success = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { firstName = '', email = '' } = state || {};
  const tz = useMemo(getTimezoneInfo, []);

  useEffect(() => {
    document.title = "You're In | Order Flow School";
    window.scrollTo(0, 0);
    trackBookingPageViewed();
  }, []);

  useCalendlyEventListener({
    onEventScheduled: () => {
      trackCalendlyBookingCompleted();
      if (email) axios.post(`${API_URL}/api/applications/booking-confirmed`, { email }).catch(() => {});
      navigate('/booking-confirmed');
    },
  });

  return (
    <>
      <style>{`
        @keyframes s-pulse-k { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
        .s-pulse-dot { width:8px; height:8px; border-radius:50%; background:#00D97E; animation: s-pulse-k 1.5s infinite; }
        .s-fade { opacity:0; transform:translateY(20px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .s-in { opacity:1; transform:translateY(0); }
        /* Calendly: kill internal scrollbars */
        .s-cal-wrap, .s-cal-wrap > div, .s-cal-wrap iframe {
          overflow: visible !important;
        }
        .s-cal-wrap iframe {
          width: 100% !important;
          height: 700px !important;
          min-width: 320px !important;
        }
        @media (max-width: 640px) {
          .s-sticky-bar {
            flex-direction: column !important;
            gap: 10px !important;
            padding: 12px 16px !important;
            text-align: center;
          }
          .s-sticky-btn {
            width: 100% !important;
            justify-content: center;
          }
        }
      `}</style>

      <div className="min-h-screen" style={{ background: '#000000', color: '#F0F4FA', fontFamily: "'Inter',sans-serif" }} data-testid="success-page">
        {/* ══════════════ NAV ══════════════ */}
        <nav className="g-nav" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} className="flex flex-col leading-none" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 26, color: '#C9A235' }}>OFS</span>
            <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 7, letterSpacing: '0.25em', color: 'rgba(201,162,53,0.6)', textTransform: 'uppercase', marginTop: -1 }}>ORDER FLOW SCHOOL</span>
          </a>
        </nav>

        {/* ══════════════ 1. TOP HERO ══════════════ */}
        <section className="pt-28 pb-14 md:pt-36 md:pb-20 relative" data-testid="success-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="space-y-8">
              {/* Green approved pill */}
              <div className="flex justify-center">
                <span
                  className="inline-flex items-center"
                  style={{
                    border: '1px solid rgba(0,217,126,0.3)',
                    background: 'rgba(0,217,126,0.08)',
                    borderRadius: 9999, padding: '6px 16px',
                    fontFamily: "'Inter',sans-serif", fontWeight: 700,
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.18em',
                    color: '#00D97E',
                  }}
                  data-testid="approved-pill"
                >
                  <span className="s-pulse-dot" style={{ marginRight: 10 }} />
                  Application Approved — One Step Left
                </span>
              </div>

              {/* Headline */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight"
                style={{ fontFamily: "'League Spartan',sans-serif", letterSpacing: '-0.01em', color: '#FFFFFF' }}
                data-testid="success-heading"
              >
                YOU'VE BEEN APPROVED FOR{' '}
                <span style={{ color: '#C9A235' }}>OFS ACCELERATOR</span>.
              </h1>

              {/* Subhead */}
              <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: '#C8C8C8' }}>
                Your next step is simple — book your call with Edgar Alvarez, Founder &amp; CEO of Order Flow School, below.
              </p>

              {/* Two-button anchor nav (replaces former testimonial card) */}
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginTop: 32,
                  marginBottom: 32,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  maxWidth: 500,
                }}
                data-testid="success-anchor-nav"
              >
                <button
                  type="button"
                  onClick={() => document.getElementById('whats-included')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="anchor-whats-included"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(201,162,53,0.5)',
                    borderRadius: 9999,
                    padding: '12px 28px',
                    color: '#C9A235',
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,162,53,0.08)'; e.currentTarget.style.borderColor = '#C9A235'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(201,162,53,0.5)'; }}
                >
                  What's Included
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById('book-your-call')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="anchor-book-your-call"
                  style={{
                    background: 'linear-gradient(135deg, #C9A235 0%, #E8C060 45%, #C9A235 100%)',
                    border: 'none',
                    borderRadius: 9999,
                    padding: '12px 28px',
                    color: '#1A0F00',
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 800,
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(201,162,53,0.3)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
                >
                  Next Step: Book Your Call &rarr;
                </button>
              </div>
            </div>
          </div>

          {/* subtle gold glow */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ top: 0 }}>
            <div
              className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full"
              style={{ background: 'radial-gradient(ellipse, rgba(201,162,53,0.06) 0%, transparent 70%)' }}
            />
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ 2. WARNING + TIMEZONE BLOCKS ══════════════ */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
            {/* Red warning */}
            <Reveal>
              <div
                className="rounded-xl p-6 h-full"
                style={{
                  background: '#111111',
                  border: '1px solid rgba(198,63,92,0.25)',
                  borderLeft: '3px solid #C63F5C',
                  borderRadius: 12,
                }}
                data-testid="et-warning-card"
              >
                <p
                  className="mb-3"
                  style={{
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 700, fontSize: 12,
                    textTransform: 'uppercase', letterSpacing: '0.16em',
                    color: '#C63F5C',
                  }}
                >
                  &#9888; Important — Read First
                </p>
                <p className="text-base leading-relaxed" style={{ color: '#F0F4FA', fontWeight: 600 }}>
                  Calls run on Eastern Time (ET).
                </p>
                <p className="text-sm leading-relaxed mt-2" style={{ color: '#C8C8C8' }}>
                  Missing your slot without 24hr notice removes you from the list permanently. Choose a time you can actually keep.
                </p>
              </div>
            </Reveal>

            {/* Gold timezone block */}
            <Reveal delay={80}>
              <div
                className="rounded-xl p-6 h-full"
                style={{
                  background: '#111111',
                  border: '1px solid rgba(201,162,53,0.3)',
                  borderLeft: '3px solid #C9A235',
                  borderRadius: 12,
                }}
                data-testid="timezone-card"
              >
                <p
                  className="mb-3"
                  style={{
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 700, fontSize: 12,
                    textTransform: 'uppercase', letterSpacing: '0.16em',
                    color: '#C9A235',
                  }}
                >
                  Your Timezone
                </p>
                {tz.absDiff >= 0.5 && tz.localStart ? (
                  <>
                    <p className="text-base leading-relaxed" style={{ color: '#F0F4FA', fontWeight: 600 }}>
                      Calls run 9 AM – 8 PM ET.
                    </p>
                    <p className="text-sm leading-relaxed mt-2" style={{ color: '#C8C8C8' }}>
                      That's approximately{' '}
                      <strong style={{ color: '#C9A235' }}>
                        {tz.localStart} – {tz.localEnd}
                      </strong>{' '}
                      your local time.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-base leading-relaxed" style={{ color: '#F0F4FA', fontWeight: 600 }}>
                      Calls run 9 AM – 8 PM Eastern Time.
                    </p>
                    <p className="text-sm leading-relaxed mt-2" style={{ color: '#C8C8C8' }}>
                      All slot times below are shown in your local timezone automatically.
                    </p>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ 3. BOOK YOUR CALL ══════════════ */}
        <section className="py-16 md:py-20" id="book-your-call">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center space-y-4 mb-10">
                <Label>Step 2 of 2</Label>
                <h2
                  className="text-3xl md:text-5xl leading-tight"
                  style={{ fontFamily: "'League Spartan',sans-serif", letterSpacing: '-0.01em', color: '#FFFFFF' }}
                >
                  BOOK YOUR CALL BELOW
                </h2>
                <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: '#C8C8C8' }}>
                  Choose a time that works. You'll get a confirmation email immediately — check your spam if you don't see it.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div
                className="s-cal-wrap rounded-xl"
                style={{
                  background: '#111111',
                  border: '1px solid rgba(201,162,53,0.18)',
                  borderRadius: 12,
                  padding: 8,
                  overflow: 'visible',
                }}
                data-testid="calendly-widget-container"
              >
                <InlineWidget
                  url={CALENDLY_URL}
                  prefill={{ name: firstName, email }}
                  pageSettings={{
                    backgroundColor: '111111',
                    primaryColor: 'C9A235',
                    textColor: 'F0F4FA',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    hideGdprBanner: true,
                  }}
                  styles={{ minWidth: '320px', height: '700px', overflow: 'visible' }}
                />
              </div>
              <p
                className="text-center mt-6"
                style={{
                  fontFamily: "'Inter',sans-serif",
                  fontSize: 12, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: '#4A5568',
                }}
                data-testid="et-reminder-text"
              >
                All times shown are in your local timezone &middot; Source: Eastern Time
              </p>
            </Reveal>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ 4. WHAT HAPPENS NEXT ══════════════ */}
        <section className="py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="space-y-4 mb-10 text-center">
                <Label>What Happens Next</Label>
                <h2
                  className="text-3xl md:text-4xl leading-tight"
                  style={{ fontFamily: "'League Spartan',sans-serif", letterSpacing: '-0.01em', color: '#FFFFFF' }}
                >
                  THREE SIMPLE STEPS.
                </h2>
              </div>
            </Reveal>

            <Reveal>
              <div
                className="rounded-xl p-8 md:p-10"
                style={{
                  background: '#111111',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 12,
                }}
                data-testid="what-happens-next"
              >
                <div className="space-y-6">
                  {[
                    'Check your email — confirmation and short prep questions arrive within minutes.',
                    "Reply to the confirmation email so we know you're locked in.",
                    "Show up ready — we'll handle the rest.",
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-5">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: '#C9A235', color: '#1A0F00' }}
                      >
                        <span style={{ fontFamily: "'League Spartan',sans-serif", fontSize: 18, lineHeight: 1 }}>{i + 1}</span>
                      </div>
                      <p
                        className="text-base md:text-lg leading-relaxed"
                        style={{ color: '#F0F4FA', paddingTop: 4 }}
                      >
                        {t}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ 5. FEATURE CARDS ══════════════ */}
        <section className="py-16 md:py-24" id="whats-included">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="space-y-4 mb-12 text-center">
                <Label>What's Inside</Label>
                <h2
                  className="text-3xl md:text-4xl leading-tight"
                  style={{ fontFamily: "'League Spartan',sans-serif", letterSpacing: '-0.01em', color: '#FFFFFF' }}
                >
                  HERE'S WHAT YOU'RE GETTING ACCESS TO.
                </h2>
                <p className="text-base md:text-lg" style={{ color: '#C8C8C8' }}>
                  Everything inside OFS Accelerator — built for one outcome.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              {features.map((f, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div
                    className="rounded-xl p-6 space-y-4 h-full transition-all duration-300"
                    style={{
                      background: '#111111',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 12,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,162,53,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                    data-testid={`success-feature-card-${i}`}
                  >
                    <FeatureIcon d={f.icon} />
                    <h3
                      className="text-lg"
                      style={{
                        fontFamily: "'Inter',sans-serif",
                        fontWeight: 700, letterSpacing: '0.08em',
                        color: '#FFFFFF', textTransform: 'uppercase',
                      }}
                    >
                      {f.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#C8C8C8' }}>
                      {f.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <p
                className="text-center mt-12"
                style={{
                  fontFamily: "'Inter',sans-serif", fontSize: 14, color: '#C8C8C8',
                }}
              >
                Questions? DM us on Instagram &rarr;{' '}
                <a
                  href="https://www.instagram.com/orderflowschool"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#C9A235', textDecoration: 'none', fontWeight: 600 }}
                >
                  @orderflowschool
                </a>
              </p>
            </Reveal>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ 6. FOOTER ══════════════ */}
        <div style={{ paddingBottom: 100 }}>
          <Footer />
        </div>

        {/* ══════════════ STICKY CTA BAR ══════════════ */}
        <div
          className="s-sticky-bar"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 999,
            background: '#0C0C0C',
            borderTop: '1px solid rgba(201,162,53,0.2)',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
          data-testid="success-sticky-bar"
        >
          <div className="s-sticky-text">
            <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 14, color: '#F0F4FA', margin: 0 }}>
              OFS Accelerator
            </p>
            <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400, fontSize: 12, color: '#8D97A8', margin: 0 }}>
              Private access &middot; Limited spots each week
            </p>
          </div>
          <button
            type="button"
            onClick={() => document.getElementById('book-your-call')?.scrollIntoView({ behavior: 'smooth' })}
            className="s-sticky-btn"
            data-testid="sticky-book-button"
            style={{
              background: 'linear-gradient(135deg, #C9A235 0%, #E8C060 45%, #C9A235 100%)',
              color: '#1A0F00',
              fontFamily: "'Inter',sans-serif",
              fontWeight: 800,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              borderRadius: 9999,
              padding: '10px 24px',
              border: 'none',
              boxShadow: '0 2px 12px rgba(201,162,53,0.3)',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
          >
            Book Your Call &rarr;
          </button>
        </div>
      </div>
    </>
  );
};

export default Success;
